import { useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import dayjs from '../../../../config/dayjs';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';

import { getAvailableData, getDashboardStats } from "../../../../services/dashboardService";

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import CANDIDATES_ICON from '../../../../assets/images/candidates.svg';
import EXAM_ICON from '../../../../assets/images/exam.svg';
import SERVER_ICON from '../../../../assets/images/server.svg';
import EXAM_HALL_ICON from '../../../../assets/images/examHall.svg';
import DISK_GREEN from '../../../../assets/images/disk-green.svg';
import DISK_PINK from '../../../../assets/images/disk-pink.svg';

import { useContext, useEffect, useState } from 'react';
import { ProjectContext } from '../../../../providers/projectProvider';
import RadiusButton from '../../../../components/RadiusButton';


export const Dashboard = () => {

    const params = useParams();
    const navigate = useNavigate();
    const projectName = params?.projectName;

    const { projectDetails } = useContext(ProjectContext);

    const [dashboardStats, setDashboardStats] = useState({
        exams: [],
        servers: [],
        examHalls: [],
        candidates: [],
        examDataDownloadedCount: 0,
        examDataDownloadedPercentage: 0,
        examDataUploadedCount: 0,
        examDataUploadedPercentage: 0,
        loggedInCandidatesCount: 0,
        loggedInCandidatesPercentage: 0,
        biometricCapturedCount: 0,
        biometricCapturedPercentage: 0
    });
    const [allData, setAllData] = useState({
        drives: [],
        courses: [],
        batches: [],
        projectMasters: [],
        servers: [],
        examHalls: []
    });
    const defaultDrive = { id: 0, driveName: 'None' };
    const defaultCourse = { id: 0, courseName: 'None' };
    const defaultBatch = { id: 0, batchName: 'None' };
    const defaultProjectMaster = { id: 0, examDate: 'None', displayExamDate: 'None', driveId: 0, courseId: 0, batchId: 0 };
    const defaultExamHall = { id: 0, hallName: 'None', projectMasterId: 0 };

    const [drives, setDrives] = useState([defaultDrive]);
    const [selectedDrive, setSelectedDrive] = useState(defaultDrive);
    const [allCourses, setAllCourses] = useState([defaultCourse]);
    const [courses, setCourses] = useState([defaultCourse]);
    const [selectedCourse, setSelectedCourse] = useState(defaultCourse);
    const [allBatches, setAllBatches] = useState([defaultBatch]);
    const [batches, setBatches] = useState([defaultBatch]);
    const [selectedBatch, setSelectedBatch] = useState(defaultBatch);
    const [allProjectMasters, setAllProjectMasters] = useState([defaultProjectMaster]);
    const [projectMasters, setProjectMasters] = useState([defaultProjectMaster]);
    const [selectedProjectMaster, setSelectedProjectMaster] = useState(defaultProjectMaster);
    const [examHalls, setExamHalls] = useState([defaultExamHall]);
    const [selectedExamHall, setSelectedExamHall] = useState(defaultExamHall);
    const isVenueBased = projectDetails?.examMode === 'VENUE_BASED';

    const fetchDashboardStats = async () => {
        try {
            const body = {
                driveId: selectedDrive?.id ? selectedDrive.id : 0,
                courseId: selectedCourse?.id ? selectedCourse.id : 0,
                batchId: selectedBatch?.id ? selectedBatch.id : 0,
                projectMasterId: selectedProjectMaster?.id ? selectedProjectMaster.id : 0,
                examHallId: selectedExamHall?.id ? selectedExamHall.id : 0,
            }
            const res = await getDashboardStats(body);

            if (res.code === 'OK') {
                let examDataDownloadedCount = 0;
                let examDataDownloadedPercentage = 0;
                let examDataUploadedCount = 0;
                let examDataUploadedPercentage = 0;
                if (res.data.servers?.length) {
                    examDataDownloadedCount = res.data.servers.filter((server) => server.examDataDownloaded).length;
                    examDataDownloadedPercentage = parseFloat(((examDataDownloadedCount * 100) / res.data.servers.length).toFixed(2))
                    examDataUploadedCount = res.data.servers.filter((server) => server.examDataUploaded).length;
                    examDataUploadedPercentage = parseFloat(((examDataUploadedCount * 100) / res.data.servers.length).toFixed(2))
                }
                let loggedInCandidatesCount = 0;
                let loggedInCandidatesPercentage = 0;
                if (res.data.candidates.length) {
                    loggedInCandidatesCount = res.data.candidates.filter((candidate) => candidate.loggedInCount).length;
                    loggedInCandidatesPercentage = parseFloat(((loggedInCandidatesCount * 100) / res.data.candidates.length).toFixed(2));
                }

                setDashboardStats({
                    exams: res.data.exams.map((exam) => {
                        const candidates = res.data.candidates.filter((candidate) => candidate.projectMasterId === exam.id);
                        const loggedInCandidatesCount = candidates.filter((candidate) => candidate.loggedInCount).length;
                        const loggedInCandidatesPercentage = parseFloat(((loggedInCandidatesCount * 100) / candidates.length).toFixed(2));
                        exam.candidates = candidates;
                        exam.loggedInCandidatesCount = candidates.length ? loggedInCandidatesCount : 0;
                        exam.loggedInCandidatesPercentage = candidates.length ? loggedInCandidatesPercentage : 0;
                        return exam;
                    }),
                    servers: res.data.servers,
                    examHalls: res.data.examHalls,
                    candidates: res.data.candidates,
                    examDataDownloadedCount: examDataDownloadedCount,
                    examDataDownloadedPercentage: examDataDownloadedPercentage,
                    examDataUploadedCount: examDataUploadedCount,
                    examDataUploadedPercentage: examDataUploadedPercentage,
                    loggedInCandidatesCount: loggedInCandidatesCount,
                    loggedInCandidatesPercentage: loggedInCandidatesPercentage,
                    biometricCapturedCount: 0,
                    biometricCapturedPercentage: 0,
                })
            }
        } catch (error) {
            console.log('There is an error while fetching dashboard statistics', error);
        }
    }

    useEffect(() => {
        const fetchAvailableData = async () => {
            try {
                const response = await getAvailableData();
                if (response.code === "OK") {
                    const data = response.data;
                    setDrives([defaultDrive, ...data.drives]);
                    setAllCourses(data.courses);
                    setAllBatches(data.batches);
                    setAllProjectMasters(data.projectMasters);
                    setAllData({
                        drives: data.drives,
                        courses: data.courses,
                        batches: data.batches,
                        projectMasters: data.projectMasters.map((pm) => {
                            pm.displayExamDate = dayjs(pm.examDate).format('DD-MM-YYYY');
                            return pm;
                        }),
                        servers: data.servers,
                        examHalls: data.examHalls,
                    });
                }
            } catch (error) {
                console.log('There is an error while getting available data', error);
            }
        }
        fetchAvailableData();
        fetchDashboardStats();
    }, []);

    useEffect(() => {
        const filteredCourses = [];
        if (selectedDrive?.id) {
            for (let pm of allProjectMasters) {
                if (pm.driveId === selectedDrive.id) {
                    const course = allCourses.find((course) => course.id === pm.courseId);
                    if (course && !filteredCourses.some((c) => c.id === course.id)) {
                        filteredCourses.push(course);
                    }
                }
            }
        }
        setCourses([defaultCourse, ...filteredCourses]);
        setSelectedCourse(defaultCourse);
        setBatches([defaultBatch]);
        setSelectedBatch(defaultBatch);
        setProjectMasters([defaultProjectMaster]);
        setSelectedProjectMaster(defaultProjectMaster);
        setExamHalls([defaultExamHall]);
        setSelectedExamHall(defaultExamHall);
    }, [selectedDrive]);

    useEffect(() => {
        const filteredBatches = [];
        if (selectedCourse?.id) {
            for (let pm of allProjectMasters) {
                if (pm.driveId === selectedDrive.id && pm.courseId === selectedCourse.id) {
                    const batch = allBatches.find((batch) => batch.id === pm.batchId);
                    if (batch && !filteredBatches.some((c) => c.id === batch.id)) {
                        filteredBatches.push(batch);
                    }
                }
            }
        }
        setBatches([defaultBatch, ...filteredBatches]);
        setSelectedBatch(defaultBatch);
        setProjectMasters([defaultProjectMaster]);
        setSelectedProjectMaster(defaultProjectMaster);
        setExamHalls([defaultExamHall]);
        setSelectedExamHall(defaultExamHall);
    }, [selectedCourse]);

    useEffect(() => {
        let projectMasters = [defaultProjectMaster];
        if (selectedDrive?.id && selectedCourse?.id && selectedBatch?.id) {
            const filteredPms = allData.projectMasters.filter((pm) => pm.driveId === selectedDrive.id && pm.courseId === selectedCourse.id && pm.batchId === selectedBatch.id);
            projectMasters = [...projectMasters, ...filteredPms];
        }

        setProjectMasters(projectMasters);
        setSelectedProjectMaster(defaultProjectMaster);
    }, [selectedDrive, selectedCourse, selectedBatch]);

    useEffect(() => {
        let examHalls = [defaultExamHall];
        if (selectedProjectMaster?.id) {
            const filteredExamHalls = allData.examHalls.filter((eh) => eh.projectMasterId === selectedProjectMaster.id);
            examHalls = [...examHalls, ...filteredExamHalls];
        }

        setExamHalls(examHalls);
        setSelectedExamHall(defaultExamHall);
    }, [selectedProjectMaster]);

    const handleDriveChange = (driveId) => {
        const drive = drives.find((d) => d.id === driveId);
        setSelectedDrive(drive);
        setSelectedProjectMaster(defaultProjectMaster);
        setSelectedExamHall(defaultExamHall);
    }
    const handleCourseChange = (courseId) => {
        const course = courses.find((c) => c.id === courseId);
        setSelectedCourse(course);
        setSelectedProjectMaster(defaultProjectMaster);
        setSelectedExamHall(defaultExamHall);
    }
    const handleBatchChange = (batchId) => {
        const batch = batches.find((b) => b.id === batchId);
        setSelectedBatch(batch);
        setSelectedProjectMaster(defaultProjectMaster);
        setSelectedExamHall(defaultExamHall);
    }
    const handleProjectMasterChange = (projectMasterId) => {
        const projectMaster = projectMasters.find((pm) => pm.id === projectMasterId);
        setSelectedProjectMaster(projectMaster);
        setSelectedExamHall(defaultExamHall);
    }
    const handleExamHallChange = (examHallId) => {
        const examHall = examHalls.find((eh) => eh.id === examHallId);
        setSelectedExamHall(examHall);
    }

    const onGoToExamSummary = (exam) => {
        navigate(`/${projectName}/exam-summary`, {
            state: {
                projectMasterId: exam.id,
            }
        });
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Dashboard</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container>
                <h1 className='title'>Dashboard</h1>

                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2,
                }}>
                    <FormControl className='form-field' sx={{ width: 222 }}>
                        <InputLabel id="driveName">Drive Name</InputLabel>
                        <Select
                            labelId="driveName"
                            id="drive"
                            label='Drive Name'
                            name='drive'
                            size='small'
                            value={selectedDrive?.id ?? 0}
                            onChange={(e, event) => handleDriveChange(event.props.value)}
                        >
                            {
                                drives.map(
                                    (drive) => <MenuItem key={drive.id} value={drive.id}>
                                        {drive.driveName}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>

                    <FormControl className='form-field' sx={{ width: 222 }}>
                        <InputLabel id="courseName">Course Name</InputLabel>
                        <Select
                            labelId="courseName"
                            id="course"
                            label='Course Name'
                            name='course'
                            size='small'
                            value={selectedCourse?.id ?? 0}
                            onChange={(e, event) => handleCourseChange(event.props.value)}
                        >
                            {
                                courses.map(
                                    (course) => <MenuItem key={course.id} value={course.id}>
                                        {course.courseName}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>

                    <FormControl className='form-field' sx={{ width: 222 }}>
                        <InputLabel id="batchName">Batch Name</InputLabel>
                        <Select
                            labelId="batchName"
                            id="batch"
                            label='Batch Name'
                            name='batch'
                            size='small'
                            value={selectedBatch?.id ?? 0}
                            onChange={(e, event) => handleBatchChange(event.props.value)}
                        >
                            {
                                batches.map(
                                    (batch) => <MenuItem key={batch.id} value={batch.id}>
                                        {batch.batchName}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>

                    <FormControl className='form-field' sx={{ width: 222 }}>
                        <InputLabel id="projectMasterDate">Exam Date</InputLabel>
                        <Select
                            labelId="projectMasterDate"
                            id="projectMaster"
                            label='Exam Date'
                            name='projectMaster'
                            size='small'
                            value={selectedProjectMaster?.id ?? 0}
                            onChange={(e, event) => handleProjectMasterChange(event.props.value)}
                        >
                            {
                                projectMasters.map(
                                    (projectMaster) => <MenuItem key={projectMaster.id} value={projectMaster.id}>
                                        {projectMaster.displayExamDate}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>

                    <FormControl className='form-field' sx={{ width: 222 }}>
                        <InputLabel id="examHallName">Exam Hall</InputLabel>
                        <Select
                            labelId="examHallName"
                            id="examHall"
                            label='Exam Hall'
                            name='examHall'
                            size='small'
                            value={selectedExamHall?.id ?? 0}
                            onChange={(e, event) => handleExamHallChange(event.props.value)}
                        >
                            {
                                examHalls.map(
                                    (examHall) => <MenuItem key={examHall.id} value={examHall.id}>
                                        {examHall.hallName}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>
                    <RadiusButton
                        type='submit'
                        variant='contained'
                        color='secondary'
                        onClick={fetchDashboardStats}
                    >
                        Go
                    </RadiusButton>
                </Box>

                <Box sx={{
                    width: '100%',
                    maxWidth: '100%',
                    display: 'grid',
                    flexWrap: 'wrap',
                    gridTemplateColumns: '1fr minmax(280px, 320px)',
                    gap: 2,
                }}>
                    <Box sx={{
                        width: '100%',
                        bgcolor: 'background.base',
                        p: 1.5,
                        borderRadius: 2,
                    }}>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Over all summary</Typography>

                        <Box className='dashboard-cards-wrapper'>
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                bgcolor: 'primary.50',
                                p: 1.5,
                                border: '1px solid',
                                borderColor: 'primary.main',
                                borderRadius: 2,
                            }}>
                                <Box sx={{
                                    display: 'grid',
                                    height: 38,
                                    width: 38,
                                    placeContent: 'center',
                                    bgcolor: '#3F41D1',
                                    borderRadius: '50%',
                                }}>
                                    <img style={{ height: 20, width: 20 }} src={CANDIDATES_ICON} alt='' />
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Total Candidates</Typography>
                                    <Typography variant='h6' sx={{ fontSize: 18 }}>
                                        {dashboardStats?.candidates?.length ? dashboardStats.candidates.length : '0'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                bgcolor: 'primary.50',
                                p: 1.5,
                                border: '1px solid',
                                borderColor: 'primary.main',
                                borderRadius: 2,
                            }}>
                                <Box sx={{
                                    display: 'grid',
                                    height: 38,
                                    width: 38,
                                    placeContent: 'center',
                                    bgcolor: '#3F41D1',
                                    borderRadius: '50%',
                                }}>
                                    <img style={{ height: 20, width: 20 }} src={EXAM_ICON} alt='' />
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Exams</Typography>
                                    <Typography variant='h6' sx={{ fontSize: 18 }}>
                                        {dashboardStats?.exams?.length ? dashboardStats.exams.length : '0'}
                                    </Typography>
                                </Box>
                            </Box>
                            {isVenueBased && (
                                <Box sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    bgcolor: 'primary.50',
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: 2,
                                }}>

                                    <Box sx={{
                                        display: 'grid',
                                        height: 38,
                                        width: 38,
                                        placeContent: 'center',
                                        bgcolor: '#3F41D1',
                                        borderRadius: '50%',
                                    }}>
                                        <img style={{ height: 20, width: 20 }} src={SERVER_ICON} alt='' />
                                    </Box>

                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                    }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Servers</Typography>
                                        <Typography variant='h6' sx={{ fontSize: 18 }}>
                                            {dashboardStats?.servers?.length ? dashboardStats.servers.length : '0'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            {isVenueBased && (
                                <Box sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    bgcolor: 'primary.50',
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: 2,
                                }}>
                                    <Box sx={{
                                        display: 'grid',
                                        height: 38,
                                        width: 38,
                                        placeContent: 'center',
                                        bgcolor: '#3F41D1',
                                        borderRadius: '50%',
                                    }}>
                                        <img style={{ height: 20, width: 20 }} src={EXAM_HALL_ICON} alt='' />
                                    </Box>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                    }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Exam Halls</Typography>
                                        <Typography variant='h6' sx={{ fontSize: 18 }}>
                                            {dashboardStats?.examHalls?.length ? dashboardStats.examHalls.length : '0'}
                                        </Typography>
                                    </Box>

                                </Box>
                            )}
                        </Box>

                        {isVenueBased && (
                            <>
                                <Typography variant='subtitle2' sx={{ mb: 1 }}>Summary by Centers</Typography>

                                <Box className='dashboard-cards-wrapper'>
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexDirection: 'column',
                                        p: 2,
                                        backgroundColor: 'grey.100',
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'grey.400',
                                    }}>
                                        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                            <CircularProgress color='success'
                                                size={100}
                                                thickness={6}
                                                variant="determinate"
                                                value={dashboardStats.examDataDownloadedPercentage}
                                            />
                                            <Box sx={{
                                                display: 'grid',
                                                placeContent: 'center',
                                                position: 'absolute',
                                                inset: 0,
                                            }}>
                                                <Typography variant="subtitle2">
                                                    {`${dashboardStats.examDataDownloadedPercentage}%`}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Typography variant='subtitle2'>Exam data downloaded</Typography>
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>Total</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        {dashboardStats.servers.length}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>In Progress</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        0
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>Not Started</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        {dashboardStats.servers.length - dashboardStats.examDataDownloadedCount}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_GREEN} alt='' />
                                                    <Typography>Completed</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        {dashboardStats.examDataDownloadedCount}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexDirection: 'column',
                                        p: 2,
                                        backgroundColor: 'grey.100',
                                        border: '1px solid',
                                        borderColor: 'grey.400',
                                        borderRadius: 2,
                                    }}>
                                        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                            <CircularProgress color='success'
                                                size={100}
                                                thickness={6}
                                                variant="determinate"
                                                value={dashboardStats.examDataUploadedPercentage}
                                            />
                                            <Box sx={{
                                                display: 'grid',
                                                placeContent: 'center',
                                                position: 'absolute',
                                                inset: 0,
                                            }}>
                                                <Typography variant="subtitle2">
                                                    {`${dashboardStats.examDataUploadedPercentage}%`}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Typography variant='subtitle2'>Exam data uploaded</Typography>
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>Total</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        {dashboardStats.servers.length}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>In Progress</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        0
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>Not Started</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        {dashboardStats.servers.length - dashboardStats.examDataUploadedCount}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_GREEN} alt='' />
                                                    <Typography>Completed</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        {dashboardStats.examDataUploadedCount}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexDirection: 'column',
                                        p: 2,
                                        backgroundColor: 'grey.100',
                                        border: '1px solid',
                                        borderColor: 'grey.400',
                                        borderRadius: 2,
                                    }}>
                                        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                            <CircularProgress color='success'
                                                size={100}
                                                thickness={6}
                                                variant="determinate"
                                                value={dashboardStats.biometricCapturedCount}
                                            />
                                            <Box sx={{
                                                display: 'grid',
                                                placeContent: 'center',
                                                position: 'absolute',
                                                inset: 0,
                                            }}>
                                                <Typography variant="subtitle2">
                                                    {`${dashboardStats.biometricCapturedCount}%`}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Typography variant='subtitle2'>Biometric Captured</Typography>
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>Total</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        {dashboardStats.candidates.length}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                                    <Typography>Not Started</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        0
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <img style={{ height: 14, width: 14 }} src={DISK_GREEN} alt='' />
                                                    <Typography>Completed</Typography>
                                                    <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                        0
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        )}
                        <Box className='dashboard-cards-wrapper'>
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexDirection: 'column',
                                p: 2,
                                backgroundColor: 'grey.100',
                                border: '1px solid',
                                borderColor: 'grey.400',
                                borderRadius: 2,
                            }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                                    <CircularProgress color='success'
                                        size={100}
                                        thickness={6}
                                        variant="determinate"
                                        value={dashboardStats.loggedInCandidatesPercentage}
                                    />
                                    <Box sx={{
                                        display: 'grid',
                                        placeContent: 'center',
                                        position: 'absolute',
                                        inset: 0,
                                    }}>
                                        <Typography variant="subtitle2">
                                            {`${dashboardStats.loggedInCandidatesPercentage}%`}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography variant='subtitle2'>Candidate Logged In</Typography>
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                            <Typography>Total</Typography>
                                            <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                {dashboardStats.candidates.length}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <img style={{ height: 14, width: 14 }} src={DISK_PINK} alt='' />
                                            <Typography>Not Started</Typography>
                                            <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                {dashboardStats.candidates.length - dashboardStats.loggedInCandidatesCount}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <img style={{ height: 14, width: 14 }} src={DISK_GREEN} alt='' />
                                            <Typography>Completed</Typography>
                                            <Typography sx={{ ml: 'auto', fontWeight: 700 }}>
                                                {dashboardStats.loggedInCandidatesCount}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'block',
                        width: 'auto',
                        flexShrink: 0,
                        flexGrow: 1,
                        minWidth: 300,
                        p: 1.5,
                        bgcolor: 'background.base',
                        borderRadius: 2
                    }}>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Exam summary</Typography>
                        <Box sx={{
                            maxWidth: '100%'
                        }}>

                            {dashboardStats.exams.map((exam, index) => <Box key={index} sx={{
                                width: '100%',
                                p: 1,
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.400',
                                maxWidth: 300,
                                mb: 1.25,
                                cursor: 'pointer',
                            }} onClick={() => onGoToExamSummary(exam)}>
                                <Typography sx={{ whiteSpace: 'break-spaces', fontSize: 14 }}>
                                    {exam?.['drive.driveName']} / {exam?.['course.courseName']} / {exam?.['batch.batchName']}
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}>
                                    <Typography variant='h6' sx={{ fontSize: 16, color: 'primary.main' }}>
                                        {exam.loggedInCandidatesCount} / {exam.candidates.length}
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: 'primary.main', fontWeight: 500 }}>
                                        {`${exam.loggedInCandidatesPercentage}%`}
                                    </Typography>
                                </Box>
                            </Box>)}
                        </Box>

                    </Box>
                </Box>
            </Container>
        </MainContainer>
    );
}
